import React, { useState } from "react";
import "./AddProduct.css";
import Header from "../Components/Header";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE, FILE_BASE } from "../config";


const AddProduct = () => {
  const navigate = useNavigate();

  const [images, setImages] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    categoryId: "",
    subcategory: "",
    originalPrice: "",
    stock: "",
    sku: "",
    weight: "",
    material: "",
    color: "",
    size: "",
    status: "",
    tag: "",
  });

  // handle input
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    const selectedFiles = files.slice(0, 3 - imageFiles.length);

    const previewUrls = selectedFiles.map((file) =>
      URL.createObjectURL(file)
    );

    setImages((prev) => [...prev, ...previewUrls].slice(0, 3));

    setImageFiles((prev) => [...prev, ...selectedFiles].slice(0, 3));
  };

  // upload image to backend
  const uploadImage = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await axios.post(
      `${API_BASE}/Upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    return response.data.imageUrl || response.data.url;
  };

  // submit
  const handleSubmit = async () => {
    try {
      if (
        !formData.name ||
        !formData.description ||
        !formData.categoryId ||
        !formData.originalPrice ||
        !formData.stock ||
        !formData.status
      ) {
        alert("Please fill all required fields!");
        return;
      }

      setLoading(true);

      // upload images first
      let uploadedImages = [];

      if (imageFiles.length > 0) {
        uploadedImages = await Promise.all(
          imageFiles.map(async (file, index) => {
            const imageUrl = await uploadImage(file);

            return {
              imageUrl,
              isMain: index === 0,
              imageType: "Product",
            };
          })
        );
      }

      // if no image uploaded then set default image
      if (uploadedImages.length === 0) {
        uploadedImages.push({
          imageUrl:
            "https://via.placeholder.com/500x500.png?text=No+Image",
          isMain: true,
          imageType: "Product",
        });
      }

      const payload = {
        name: formData.name,
        description: formData.description,

        categoryId: Number(formData.categoryId),

        subCategory: formData.subcategory,

        originalPrice: Number(formData.originalPrice),

        quantity: Number(formData.stock),

        sku: formData.sku,

        weight: Number(formData.weight || 0),

        material: formData.material,

        size: formData.size,

        status: Number(formData.status),

        images: uploadedImages,

        colors: formData.color
          ? [
            {
              colorName: formData.color,
              colorImageUrl:
                uploadedImages.length > 0
                  ? uploadedImages[0].imageUrl
                  : "",
            },
          ]
          : [],
      };

      await axios.post(`${API_BASE}/Products`, payload);

      alert("Product created successfully!");

      navigate("/products");
    } catch (error) {
      console.error(error);

      alert(
        error?.response?.data?.message ||
        "Failed to create product"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-product-container">
      <Header />

      <div className="form-wrapper">
        <h1>Add New Product</h1>
        <p>Add product details below</p>

        {/* IMAGES */}
        <div className="product-card">
          <h3>Product Images</h3>

          <div className="image-row">
            {[0, 1, 2].map((i) => (
              <div key={i} className="image-box">
                {images[i] && <img src={images[i]} alt="preview" />}
              </div>
            ))}
          </div>

          <label className="upload-box">
            <input
              type="file"
              hidden
              multiple
              accept="image/*"
              onChange={handleImageUpload}
            />

            <div className="upload-inner">
              <p>Click to upload images</p>
              <span>Max 3 images</span>
            </div>
          </label>
        </div>

        {/* BASIC INFO */}
        <div className="product-card">
          <h3>Basic Information</h3>

          <label>Product Name *</label>
          <input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="e.g. Banarasi Silk Saree"
          />

          <label>Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Product description..."
          />
        </div>

        {/* CATEGORY */}
        <div className="product-card">
          <h3>Category & Pricing</h3>

          <div className="grid-2">
            <div>
              <label>Category *</label>

              <select
                name="categoryId"
                value={formData.categoryId}
                onChange={handleChange}
              >
                <option value="">Select Category</option>

                {/* Replace these IDs with DB IDs */}
                <option value="1">Sarees</option>
                <option value="2">Dresses</option>
                <option value="3">Kurti</option>
                <option value="4">Lehenga</option>
              </select>
            </div>

            <div>
              <label>Subcategory</label>

              <input
                name="subcategory"
                value={formData.subcategory}
                onChange={handleChange}
                placeholder="Silk / Cotton"
              />
            </div>

            <div>
              <label>Price *</label>

              <input
                type="number"
                name="originalPrice"
                value={formData.originalPrice}
                onChange={handleChange}
                placeholder="12500"
              />
            </div>

            <div>
              <label>Stock *</label>

              <input
                type="number"
                name="stock"
                value={formData.stock}
                onChange={handleChange}
                placeholder="10"
              />
            </div>
          </div>
        </div>

        {/* STOCK DETAILS */}
        <div className="product-card">
          <h3>Stock Details</h3>

          <div className="grid-2">
            <div>
              <label>SKU</label>

              <input
                name="sku"
                value={formData.sku}
                onChange={handleChange}
                placeholder="SKU"
              />
            </div>

            <div>
              <label>Weight</label>

              <input
                type="number"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                placeholder="Weight"
              />
            </div>

            <div>
              <label>Fabric Type</label>

              <input
                name="material"
                value={formData.material}
                onChange={handleChange}
                placeholder="Material"
              />
            </div>

            <div>
              <label>Color</label>

              <input
                name="color"
                value={formData.color}
                onChange={handleChange}
                placeholder="Color"
              />
            </div>

            <div>
              <label>Size</label>

              <input
                name="size"
                value={formData.size}
                onChange={handleChange}
                placeholder="Size"
              />
            </div>

            <div>
              <label>Status *</label>

              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
              >
                <option value="">Status</option>

                {/* Update enum values according to backend enum */}
                <option value="1">Active</option>
                <option value="2">Out Of Stock</option>
              </select>
            </div>

            <div>
              <label>Tag</label>

              <input
                name="tag"
                value={formData.tag}
                onChange={handleChange}
                placeholder="Tag"
              />
            </div>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="form-actions">
          <button
            className="btn cancel"
            onClick={() => navigate("/products")}
          >
            Cancel
          </button>

          <button
            className="btn submit"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? "Creating..." : "Add Product"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddProduct;